import React, { useEffect, useMemo, useState } from 'react';
import { Star, Phone, Navigation, Search, Zap, Loader2, LocateFixed, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

// Launch-market centroids (match seeded clinic city strings exactly)
const CITY_POINTS = [
  { city: 'Portland, OR', lat: 45.5231, lng: -122.6765 },
  { city: 'Eugene, OR', lat: 44.0521, lng: -123.0868 },
  { city: 'San Francisco, CA', lat: 37.7749, lng: -122.4194 },
];

function haversineKm(a, b, c, d) {
  const R = 6371, toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(c - a), dLng = toRad(d - b);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a)) * Math.cos(toRad(c)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

function geoToCity(lat, lng) {
  let best = null, bestD = Infinity;
  for (const p of CITY_POINTS) {
    const dkm = haversineKm(lat, lng, p.lat, p.lng);
    if (dkm < bestD) { bestD = dkm; best = p.city; }
  }
  return bestD <= 300 ? best : null; // ~185mi radius of a launch market
}

function zipToCity(zip) {
  if (!/^\d{5}$/.test(zip)) return undefined; // invalid
  const p3 = zip.slice(0, 3);
  if (['941', '940', '944', '943'].includes(p3)) return 'San Francisco, CA';
  if (p3 === '974') return 'Eugene, OR';
  if (['970', '971', '972'].includes(p3)) return 'Portland, OR';
  return null; // valid ZIP but not a launch market
}

export default function FindVets() {
  const [emergencyOnly, setEmergencyOnly] = useState(false);
  const [q, setQ] = useState('');
  const [city, setCity] = useState('');
  const [cityList, setCityList] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locating, setLocating] = useState(false);
  const [showZip, setShowZip] = useState(false);
  const [zip, setZip] = useState('');

  useEffect(() => {
    (async () => {
      try { const r = await api.get('/cities'); setCityList(r.data.cities || []); } catch (e) {}
    })();
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/clinics', { params: { emergency: emergencyOnly || undefined, q: q || undefined, city: city || undefined } });
        if (active) setClinics(r.data.clinics || []);
      } catch (e) {} finally { if (active) setLoading(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [emergencyOnly, q, city]);

  const list = useMemo(() => clinics, [clinics]);

  const resolveCity = (matched, label) => {
    if (matched) { setCity(matched); toast.success(`Showing cat vets near ${matched}`); }
    else if (matched === null) { setCity(''); toast.message(`We're not in ${label || 'your area'} yet — showing all launch cities.`); }
  };

  const useMyLocation = () => {
    if (!('geolocation' in navigator)) {
      setShowZip(true);
      toast.error('Location unavailable on this device — enter your ZIP code instead.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const matched = geoToCity(pos.coords.latitude, pos.coords.longitude);
        if (matched) resolveCity(matched);
        else { setCity(''); toast.message("We're not in your area yet — showing all launch cities."); }
      },
      () => {
        setLocating(false);
        setShowZip(true);
        toast.error("Couldn't get your location — enter your ZIP code instead.");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  };

  const applyZip = () => {
    const matched = zipToCity(zip.trim());
    if (matched === undefined) { toast.error('Please enter a valid 5-digit ZIP code.'); return; }
    resolveCity(matched, `ZIP ${zip.trim()}`);
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-10 border-b border-border bg-background/95 px-4 py-3 backdrop-blur">
        <h1 className="text-base font-extrabold">Find trusted cat vets</h1>

        <div className="mt-3 flex items-center gap-2">
          <button onClick={useMyLocation} disabled={locating} data-testid="use-location-btn" className="flex h-10 items-center gap-1.5 rounded-full bg-[hsl(var(--coral))] px-3 text-sm font-bold text-white disabled:opacity-70">
            {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}Use my location
          </button>
          <button onClick={() => setShowZip((v) => !v)} data-testid="enter-zip-toggle" className="flex h-10 items-center gap-1.5 rounded-full border border-border bg-white px-3 text-sm font-bold text-foreground">
            <MapPin className="h-4 w-4" />Enter ZIP
          </button>
        </div>

        {showZip && (
          <div className="mt-2 flex items-center gap-2" data-testid="zip-row">
            <input value={zip} onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))} onKeyDown={(e) => { if (e.key === 'Enter') applyZip(); }} inputMode="numeric" placeholder="Enter ZIP code (e.g. 97201)" data-testid="zip-input" className="h-10 flex-1 rounded-full border border-input bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
            <button onClick={applyZip} data-testid="zip-apply" className="h-10 rounded-full bg-[hsl(var(--coral))] px-4 text-sm font-bold text-white">Go</button>
          </div>
        )}

        <select value={city} onChange={(e) => setCity(e.target.value)} className="mt-2 h-10 w-full rounded-full border border-input bg-white px-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]">
          <option value="">All launch cities</option>
          {cityList.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="mt-2 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clinics" className="h-10 w-full rounded-full border border-input bg-white pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
          </div>
          <button onClick={() => setEmergencyOnly((v) => !v)} className={`flex h-10 items-center gap-1.5 rounded-full px-3 text-sm font-bold transition ${emergencyOnly ? 'bg-[hsl(var(--coral))] text-white' : 'border border-border bg-white text-foreground'}`}>
            <Zap className="h-4 w-4" />Emergency
          </button>
        </div>
      </header>

      <div className="space-y-3 px-4 py-4">
        {loading && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--coral))]" /></div>}
        {!loading && list.map((c, i) => (
          <div key={c.id} className={`animate-fade-up overflow-hidden rounded-2xl border card-soft ${c.tier === 'premium' ? 'border-[hsl(var(--coral))] bg-accent/40' : c.tier === 'basic' ? 'border-[hsl(var(--coral))]/40 bg-white' : 'border-border bg-white'}`} style={{ animationDelay: `${i * 50}ms` }}>
            <div className="flex gap-3 p-3">
              {c.img ? <img src={c.img} alt={c.name} className="h-16 w-16 shrink-0 rounded-xl object-cover" /> : <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-secondary text-xl font-extrabold text-muted-foreground">{c.name[0]}</div>}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="truncate font-bold">{c.name}</h3>
                  {c.tier !== 'none' && <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-[hsl(var(--coral))] px-2 py-0.5 text-[10px] font-bold uppercase text-white"><Star className="h-2.5 w-2.5" />Featured</span>}
                </div>
                <div className="mt-0.5 flex items-center gap-1 text-sm">
                  <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                  <span className="font-semibold">{c.rating}</span>
                  <span className="text-muted-foreground">({c.reviews}) · {c.distance}</span>
                </div>
                <p className="truncate text-xs text-muted-foreground">{c.address}</p>
                <p className={`text-xs font-semibold ${c.hours.startsWith('Open') || c.hours.includes('24/7') ? 'text-emerald-600' : 'text-muted-foreground'}`}>{c.hours}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-px bg-border">
              <a href={`tel:${c.phone}`} className="flex items-center justify-center gap-1.5 bg-white py-2.5 text-sm font-bold text-[hsl(var(--coral))] transition hover:bg-accent/40"><Phone className="h-4 w-4" />Call</a>
              <button onClick={() => { window.open(`https://maps.google.com/?q=${encodeURIComponent(c.address)}`, '_blank'); toast.success('Opening directions…'); }} className="flex items-center justify-center gap-1.5 bg-white py-2.5 text-sm font-bold text-[hsl(var(--coral))] transition hover:bg-accent/40"><Navigation className="h-4 w-4" />Directions</button>
            </div>
          </div>
        ))}
        {!loading && list.length === 0 && <p className="py-16 text-center text-muted-foreground">No clinics match your search.</p>}
      </div>
    </div>
  );
}
